import { defineStore } from 'pinia'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import api from '@/services/axiosApi'

// Base variant interface with common properties
interface BaseVariant {
  id: string
  name: string
  weight?: number
  content?: any
}

// Question-specific variant
interface QuestionVariant extends BaseVariant {
  text?: string
  answers?: Record<string, { label: string; imageUrl?: string } | string>
}

// Offer-specific variant
interface OfferVariant extends BaseVariant {
  parts?: {
    title?: string
    description?: string
    buttonText?: string
    imageUrl?: string
  }
}

// Flow-specific variant
interface FlowVariant extends BaseVariant {
  funnelDefinition?: {
    type: string
    child?: any[]
    [key: string]: any
  }
}

// Union type for all variants
type SplitTestVariant = QuestionVariant | OfferVariant | FlowVariant

interface SplitTest {
  pid: string
  resource_type: string
  resource_id: string
  status: 'draft' | 'active' | 'completed'
  variants: SplitTestVariant[]
  started_at: string | null
  ended_at: string | null
  winner_id?: string | null
  snapshot?: {
    created_at: string
    original_content: any
    metrics: any
  } | null
  created_at: string
  updated_at: string
  is_active: boolean
}

interface CreateSplitTestData {
  resource_type: 'question' | 'offer' | string  // Allow other types for future expansion
  resource_id: string
  variants: SplitTestVariant[]
  status?: 'draft' | 'active' | 'completed'
  started_at?: string | null
  ended_at?: string | null
}

interface UpdateSplitTestData {
  resource_type?: string
  resource_id?: string
  status?: 'draft' | 'active' | 'completed'
  variants?: SplitTestVariant[]
  started_at?: string | null
  ended_at?: string | null
}

export const useSplitTestStore = defineStore('splitTest', () => {
  const configStore = useConfigStore()
  const { currentTeamPid } = storeToRefs(configStore)
  const queryClient = useQueryClient()
  
  // Main reactive state
  const splitTests = ref<SplitTest[]>([])
  const lastSavedTests = ref<SplitTest[]>([])
  
  // Track which tests are being saved
  const savingTests = ref<Set<string>>(new Set())

  // Query for fetching all split tests
  const splitTestsQuery = useQuery({
    queryKey: ['splitTests', currentTeamPid],
    queryFn: () =>
      api
        .get<SplitTest[]>(`/api/${currentTeamPid.value}/splitTests`)
        .then((res) => res.data),
    enabled: computed(() => !!currentTeamPid.value)
  })

  // Watch for query data and update our reactive state
  watchEffect(() => {
    if (splitTestsQuery.data.value) {
      splitTests.value = JSON.parse(JSON.stringify(splitTestsQuery.data.value))
      lastSavedTests.value = JSON.parse(JSON.stringify(splitTestsQuery.data.value))
    }
  })
  
  // Watch for changes and auto-save
  watch(
    splitTests,
    (newTests) => {
      if (newTests && lastSavedTests.value) {
        // Check each test for changes
        newTests.forEach(test => {
          const lastSaved = lastSavedTests.value.find(t => t.pid === test.pid)
          if (lastSaved && JSON.stringify(test) !== JSON.stringify(lastSaved)) {
            // console.log('Split test changed, triggering save for:', test.pid)
            debouncedUpdateTest(test)
          }
        })
      }
    },
    { deep: true }
  )

  // Get split test by ID
  const getById = (pid: string) => {
    return splitTests.value.find((test) => test.pid === pid)
  }

  // Query for fetching split tests for a specific resource
  const getSplitTestsForResource = (resourceType: string, resourceId: string) => {
    return useQuery({
      queryKey: ['splitTests', currentTeamPid.value, 'resource', resourceType, resourceId],
      queryFn: () =>
        api
          .get<SplitTest[]>(`/api/${currentTeamPid.value}/splitTests/forResource`, {
            params: {
              resource_type: resourceType,
              resource_id: resourceId
            }
          })
          .then((res) => res.data),
      enabled: computed(() => !!currentTeamPid.value && !!resourceType && !!resourceId)
    })
  }

  // Mutation for creating a split test
  const createSplitTestMutation = useMutation({
    mutationFn: (data: CreateSplitTestData) =>
      api
        .post<SplitTest>(`/api/${currentTeamPid.value}/splitTests`, data)
        .then((res) => res.data),
    onSuccess: () => {
      splitTestsQuery.refetch()
      // Also invalidate resource-specific queries
      queryClient.invalidateQueries({ queryKey: ['splitTests'] })
    }
  })

  // Mutation for updating a split test
  const updateSplitTestMutation = useMutation({
    mutationFn: ({ pid, data }: { pid: string; data: UpdateSplitTestData }) =>
      api
        .put<SplitTest>(`/api/${currentTeamPid.value}/splitTests/${pid}`, data)
        .then((res) => res.data),
    onSuccess: async (updatedTest) => {
      // Remove from saving set
      savingTests.value.delete(updatedTest.pid)

      // Find the current local state
      const mainIndex = splitTests.value.findIndex(t => t.pid === updatedTest.pid)
      if (mainIndex !== -1) {
        // Update server-managed fields from the response
        splitTests.value[mainIndex].updated_at = updatedTest.updated_at
        splitTests.value[mainIndex].started_at = updatedTest.started_at
        splitTests.value[mainIndex].ended_at = updatedTest.ended_at

        // Update lastSaved to match current local state (not server response)
        // This prevents infinite save loops while preserving local edits
        const lastSavedIndex = lastSavedTests.value.findIndex(t => t.pid === updatedTest.pid)
        if (lastSavedIndex !== -1) {
          lastSavedTests.value[lastSavedIndex] = JSON.parse(JSON.stringify(splitTests.value[mainIndex]))
        }
      }
      
      // Invalidate resource-specific queries
      queryClient.invalidateQueries({ 
        queryKey: ['splitTests', currentTeamPid.value, 'resource', updatedTest.resource_type, updatedTest.resource_id] 
      })
    },
    onError: (error, { pid }) => {
      // Remove from saving set on error
      savingTests.value.delete(pid)
      console.error('Failed to update split test:', error)
    }
  })

  // Mutation for deleting a split test
  const deleteSplitTestMutation = useMutation({
    mutationFn: (pid: string) =>
      api.delete(`/api/${currentTeamPid.value}/splitTests/${pid}`),
    onSuccess: () => {
      splitTestsQuery.refetch()
      queryClient.invalidateQueries({ queryKey: ['splitTests'] })
    }
  })

  // Mutation for starting a split test
  const startSplitTestMutation = useMutation({
    mutationFn: (pid: string) =>
      api
        .post<SplitTest>(`/api/${currentTeamPid.value}/splitTests/${pid}/start`)
        .then((res) => res.data),
    onSuccess: (updatedTest) => {
      // Update the local state immediately
      const index = splitTests.value.findIndex((test) => test.pid === updatedTest.pid)
      if (index !== -1) {
        splitTests.value[index] = updatedTest
        lastSavedTests.value[index] = JSON.parse(JSON.stringify(updatedTest))
      }
      
      // Invalidate and refetch the main query to ensure fresh data
      queryClient.invalidateQueries({ 
        queryKey: ['splitTests', currentTeamPid.value] 
      })
      
      // Also invalidate resource-specific queries
      queryClient.invalidateQueries({ 
        queryKey: ['splitTests', currentTeamPid.value, 'resource', updatedTest.resource_type, updatedTest.resource_id] 
      })
      
      // Force an immediate refetch
      splitTestsQuery.refetch()
    }
  })

  // Mutation for ending a split test
  const endSplitTestMutation = useMutation({
    mutationFn: ({ pid, winnerId }: { pid: string; winnerId: string }) =>
      api
        .post<SplitTest>(`/api/${currentTeamPid.value}/splitTests/${pid}/end`, { winner_id: winnerId })
        .then((res) => res.data),
    onSuccess: (updatedTest) => {
      // Update the local state immediately
      const index = splitTests.value.findIndex((test) => test.pid === updatedTest.pid)
      if (index !== -1) {
        splitTests.value[index] = updatedTest
        lastSavedTests.value[index] = JSON.parse(JSON.stringify(updatedTest))
      }
      
      // Invalidate and refetch the main query to ensure fresh data
      queryClient.invalidateQueries({ 
        queryKey: ['splitTests', currentTeamPid.value] 
      })
      
      // Also invalidate resource-specific queries
      queryClient.invalidateQueries({ 
        queryKey: ['splitTests', currentTeamPid.value, 'resource', updatedTest.resource_type, updatedTest.resource_id] 
      })
      
      // IMPORTANT: Invalidate the config query so offer/question updates are reflected
      // This ensures that when an offer/question variant is applied, the main config is refreshed
      queryClient.invalidateQueries({ 
        queryKey: ['config', currentTeamPid.value] 
      })
      
      // Force an immediate refetch
      splitTestsQuery.refetch()
    }
  })

  // Helper functions
  const create = (data: CreateSplitTestData) => {
    const result = createSplitTestMutation.mutateAsync(data)
    result.then((test) => {
      recordAnalyticsEvent(AnalyticsEventTypes.SPLIT_TEST_CREATED, {
        splitTestPid: test.pid,
        resourceType: data.resource_type,
        resourceId: data.resource_id,
        variantCount: data.variants?.length ?? 0
      })
    }).catch(() => {})
    return result
  }

  // Debounced update function for individual tests
  const debouncedUpdateTest = useDebounceFn((test: SplitTest) => {
    if (savingTests.value.has(test.pid)) return // Already saving
    
    savingTests.value.add(test.pid)
    
    // Deep clone the variants to ensure all nested properties are included
    const variantsToSend = JSON.parse(JSON.stringify(toRaw(test.variants)))
    
    updateSplitTestMutation.mutate({ 
      pid: test.pid, 
      data: {
        variants: variantsToSend,
        status: test.status,
        resource_type: test.resource_type,
        resource_id: test.resource_id
      }
    })
  }, 1000)
  
  const update = (pid: string, data: UpdateSplitTestData) => {
    return updateSplitTestMutation.mutateAsync({ pid, data })
  }

  const destroy = (pid: string) => {
    return deleteSplitTestMutation.mutateAsync(pid)
  }

  const start = (pid: string) => {
    const result = startSplitTestMutation.mutateAsync(pid)
    result.then((test) => {
      recordAnalyticsEvent(AnalyticsEventTypes.SPLIT_TEST_STARTED, {
        splitTestPid: test.pid,
        resourceType: test.resource_type,
        resourceId: test.resource_id,
        variantCount: test.variants?.length ?? 0
      })
    }).catch(() => {})
    return result
  }

  const end = (pid: string, winnerId: string) => {
    return endSplitTestMutation.mutateAsync({ pid, winnerId })
  }

  // Get active split test for a resource
  const getActiveSplitTestForResource = (resourceType: string, resourceId: string) => {
    return splitTests.value.find(
      (test) =>
        test.resource_type === resourceType &&
        test.resource_id === resourceId &&
        test.is_active
    )
  }
  
  // Get all split tests for a resource (used by flow builder)
  const getForResource = (resourceType: string, resourceId: string) => {
    return splitTests.value.filter(
      (test) =>
        test.resource_type === resourceType &&
        test.resource_id === resourceId
    )
  }

  // Get active or draft test for a question (excludes completed tests)
  const getActiveTestForQuestion = (questionId: string) => {
    const tests = splitTests.value.filter(test => 
      test.resource_type === 'question' && 
      test.resource_id === questionId &&
      test.status !== 'completed'
    )
    
    return tests.length > 0 ? tests[0] : null
  }
  
  // Get active or draft test for any resource (excludes completed tests)
  const getActiveTestForResource = (resourceType: string, resourceId: string) => {
    const tests = splitTests.value.filter(test => 
      test.resource_type === resourceType && 
      test.resource_id === resourceId &&
      test.status !== 'completed'
    )
    
    return tests.length > 0 ? tests[0] : null
  }
  
  // Clear state when team changes
  watch(currentTeamPid, () => {
    splitTests.value = []
    lastSavedTests.value = []
    savingTests.value.clear()
  })
  
  return {
    // State
    splitTests,
    splitTestsQuery,
    savingTests: readonly(savingTests),

    // Getters
    getById,
    getActiveSplitTestForResource,
    getSplitTestsForResource,
    getForResource,
    getActiveTestForQuestion,
    getActiveTestForResource,

    // Actions
    create,
    update,
    destroy,
    start,
    end,

    // Mutations (exposed for direct access if needed)
    createSplitTestMutation,
    updateSplitTestMutation,
    deleteSplitTestMutation,
    startSplitTestMutation,
    endSplitTestMutation
  }
})